import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Trash2, AlertTriangle, Check, Truck, Eye, Pencil } from 'lucide-react';
import '../../App.css';

const VehicleCard = ({ vehicle, onEdit, onDelete, onLogDVIR }) => {
  const navigate = useNavigate();

  const getInspectionStatus = () => {
    if (!vehicle.lastInspectionDate) {
      return { status: 'not_set', label: 'Not Set', color: '#6B7280', class: '' };
    }

    const lastInspection = new Date(vehicle.lastInspectionDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    lastInspection.setHours(0, 0, 0, 0);

    const daysSinceInspection = Math.floor(
      (today.getTime() - lastInspection.getTime()) / (1000 * 60 * 60 * 24)
    );

    if (daysSinceInspection > 365) {
      return {
        status: 'overdue',
        label: 'Overdue',
        color: '#EF4444',
        class: 'inspection-overdue',
      };
    } else if (daysSinceInspection >= 350) {
      return {
        status: 'due_soon',
        label: 'Due Soon',
        color: '#F59E0B',
        class: 'inspection-due',
      };
    } else {
      return {
        status: 'valid',
        label: 'Valid',
        color: '#10B981',
        class: '',
      };
    }
  };

  const getRegistrationStatus = () => {
    if (!vehicle.registrationExpirationDate) {
      return { status: 'not_set', label: 'Not Set', color: '#6B7280' };
    }

    const expiration = new Date(vehicle.registrationExpirationDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    expiration.setHours(0, 0, 0, 0);

    const daysUntilExpiration = Math.ceil(
      (expiration.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)
    );

    if (daysUntilExpiration < 0) {
      return { status: 'expired', label: 'Expired', color: 'var(--danger)' };
    } else if (daysUntilExpiration <= 30) {
      return { status: 'expiring', label: 'Expiring', color: 'var(--warning)' };
    } else {
      return { status: 'valid', label: 'Valid', color: 'var(--success)' };
    }
  };

  const getReadinessScoreClass = (score) => {
    if (score >= 85) return 'excellent';
    if (score >= 70) return 'good';
    return 'poor';
  };

  const getReadinessScoreColor = (score) => {
    if (score >= 85) return 'var(--success)';
    if (score >= 70) return 'var(--warning)';
    return 'var(--danger)';
  };

  const inspectionStatus = getInspectionStatus();
  const registrationStatus = getRegistrationStatus();
  const readinessScore = vehicle.readinessScore || 0;
  const scoreClass = getReadinessScoreClass(readinessScore);
  const scoreColor = getReadinessScoreColor(readinessScore);

  const displayName = vehicle.nickname || `${vehicle.year} ${vehicle.make} ${vehicle.model}`;
  const vinLast6 = vehicle.vin ? vehicle.vin.slice(-6).toUpperCase() : '';

  const cardClass = `vehicle-card ${inspectionStatus.class}`;

  return (
    <div className={cardClass} style={{ borderColor: inspectionStatus.color }}>
      <div className="vehicle-card-header">
        <div className="vehicle-icon-large"><Truck size={40} /></div>
        <div className="vehicle-actions">
          <button
            className="action-icon-button"
            onClick={() => navigate(`/vehicles/${vehicle.id}`)}
            title="View Details"
          >
            <Eye size={18} />
          </button>
          <button
            className="action-icon-button"
            onClick={() => onEdit(vehicle)}
            title="Edit"
          >
            <Pencil size={18} />
          </button>
          <button
            className="action-icon-button delete"
            onClick={() => onDelete(vehicle)}
            title="Delete"
          >
            <Trash2 size={18} />
          </button>
        </div>
      </div>

      <div className="vehicle-card-body">
        <h3 className="vehicle-name">{displayName}</h3>
        <div className="vehicle-details">
          <div className="vehicle-detail-row">
            <span className="detail-label">Plate:</span>
            <span className="detail-value">
              {vehicle.licensePlate} ({vehicle.licensePlateState})
            </span>
          </div>
          <div className="vehicle-detail-row">
            <span className="detail-label">VIN:</span>
            <span className="detail-value vin">...{vinLast6}</span>
          </div>
        </div>

        <div className="readiness-score-section">
          <div className={`readiness-score ${scoreClass}`} style={{ borderColor: scoreColor }}>
            <div className="score-inner">
              <span className="score-number" style={{ color: scoreColor }}>
                {readinessScore}
              </span>
            </div>
          </div>
          <div className="score-label">Readiness Score</div>
        </div>

        <div className="status-badges">
          <div className={`status-badge status-inspection-${inspectionStatus.status}`}>
            Inspection: {inspectionStatus.label}
          </div>
          <div className={`status-badge status-registration-${registrationStatus.status}`}>
            Registration: {registrationStatus.label}
          </div>
          {vehicle.hasOpenDefects && (
            <div className="status-badge status-defects-open">
              <><AlertTriangle size={16} style={{ verticalAlign: 'middle', marginRight: 4 }} /> Open Defects</>
            </div>
          )}
          {!vehicle.hasOpenDefects && (
            <div className="status-badge status-defects-none">
              <><Check size={16} style={{ verticalAlign: 'middle', marginRight: 4 }} /> No Defects</>
            </div>
          )}
        </div>

        <div className="vehicle-card-actions">
          <button
            className="btn btn-primary"
            onClick={() => navigate(`/vehicles/${vehicle.id}`)}
          >
            View Details
          </button>
          <button
            className="btn btn-secondary"
            onClick={() => onLogDVIR(vehicle)}
          >
            Log DVIR
          </button>
        </div>
      </div>
    </div>
  );
};

export default VehicleCard;

